import { convertToCorsProxyUrl } from '@modules/utils/download'

import { AI_COLOR_RESULT_DISCLAIMER } from '../constants'
import type { SummaryRow } from './buildSummaryRows'

export interface ComposeSummaryImageInput {
  /** タイトルとして左上に描画する案件名 */
  projectName: string
  /** 現況写真（Before）URL。CORS proxy 適用前 */
  beforeImageUrl: string
  /** 提案イメージ（After）URL。CORS proxy 適用前 */
  afterImageUrl: string
  /** 部位ごとの色／素材サマリ（変換情報）。空配列なら見出しのみ描画 */
  rows: SummaryRow[]
  /** 右上に描画する会社名（company role のみ。未指定なら描画しない） */
  companyName?: string
  /** 右上に描画する会社ロゴ URL。未設定／読込失敗時は会社名テキストのみ */
  companyLogoUrl?: string
}

/**
 * A4 横（297mm × 210mm）を 300dpi 相当で固定。
 * Before/After/ロゴは各ボックスにアスペクト比維持（contain）で配置する。
 */
const CANVAS = {
  width: 3508,
  height: 2480,
  margin: 130,
  /** 最下部の余白は控えめにする（フッターを下げて本文領域を広げる） */
  marginBottom: 64,
} as const

const FONT = {
  title: 76,
  subtitle: 36,
  company: 38,
  sectionMain: 48,
  sectionSub: 30,
  convHeading: 46,
  convSub: 28,
  row: 36,
  disclaimer: 28,
  footer: 30,
} as const

/**
 * 右上の会社ロゴ（およびロゴ無し時の会社名テキスト）の最大ボックス。
 * ヘッダー帯（margin 〜 bodyTop = margin + 250）の右側は余白なので、その高さをほぼ使い切る
 * 240px を上限にする（下端 margin + 240 = 370 は本文開始 380 より上）。
 * 幅は横長ロゴ向けの上限で、タイトルの折返し幅はこの幅ぶんを常に避ける。
 */
const LOGO = {
  maxW: 1000,
  maxH: 240,
} as const

/**
 * タイトル（プロジェクト名）のレイアウト規則。
 * 長い案件名でも全文が読めるよう、フォントを段階的に縮小しながら折り返す。
 */
const TITLE = {
  /** 全文表示する上限文字数（コードポイント基準）。超過分は末尾を "…" で省略する */
  maxChars: 100,
  /**
   * 折返し幅から差し引く右側の予約領域。
   * 右上の会社ロゴ（最大 LOGO.maxW × LOGO.maxH）と重ならないよう、ロゴ有無に関わらず一律で確保する。
   */
  reservedRightW: LOGO.maxW + 80,
  /** 行送り = フォントサイズ × この係数 */
  lineHeightRatio: 1.28,
  /**
   * 上から順に試し、行数が maxLines 以内に収まった段階を採用する。
   * 最終段（56px × 3 行）は折返し幅 2168px（= 3248 − 1080）換算で 1 行 38 文字 × 3 行 = 114 文字を
   * 収容でき、100 文字の日本語案件名が必ず全文入る。
   */
  steps: [
    { size: FONT.title, maxLines: 1 },
    { size: 64, maxLines: 2 },
    { size: 56, maxLines: 3 },
  ],
} as const

const COLORS = {
  background: '#FFFFFF',
  text: '#111827',
  muted: '#6B7280',
  faint: '#9CA3AF',
  border: '#E5E7EB',
  swatchBorder: '#D1D5DB',
  imageBg: '#F3F4F6',
  accent: '#2E8B6B',
  accentSoft: '#E6F4EC',
} as const

const SUBTITLE = 'カラーシミュレーション結果のご案内'
const CONV_HEADING = '変換情報'
const CONV_SUB = '使用した色番号'
// 画面内のディスクレーマーと同一文言にするため共通定数を使う（issue #2417）。
const DISCLAIMER = AI_COLOR_RESULT_DISCLAIMER
const FOOTER_PREFIX = 'AIカラーシミュレーション'

interface Box {
  x: number
  y: number
  w: number
  h: number
}

const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error(`Failed to load image: ${url}`))
    image.src = convertToCorsProxyUrl(url)
  })
}

/** ロゴは読み込み失敗しても全体を止めない（null で会社名テキストにフォールバック） */
const loadImageSafe = async (url: string): Promise<HTMLImageElement | null> => {
  try {
    return await loadImage(url)
  } catch (e) {
    console.warn('[composeSummaryImage] 画像読込失敗（スキップ）:', url, e)
    return null
  }
}

const drawRoundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) => {
  const r = Math.min(radius, width / 2, height / 2)
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + width - r, y)
  ctx.quadraticCurveTo(x + width, y, x + width, y + r)
  ctx.lineTo(x + width, y + height - r)
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height)
  ctx.lineTo(x + r, y + height)
  ctx.quadraticCurveTo(x, y + height, x, y + height - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}

/** 与えた幅に収まるよう text を末尾から削り、超える場合は "…" を付ける（ctx.font を事前にセット） */
const truncateToWidth = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string => {
  if (ctx.measureText(text).width <= maxWidth) return text
  const ellipsis = '…'
  let lo = 0
  let hi = text.length
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2)
    if (ctx.measureText(text.slice(0, mid) + ellipsis).width <= maxWidth) {
      lo = mid
    } else {
      hi = mid - 1
    }
  }
  return text.slice(0, lo) + ellipsis
}

/** 日本語（空白区切りなし）を 1 文字ずつ測りながら maxWidth で折り返す（ctx.font を事前にセット） */
const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
  const lines: string[] = []
  let line = ''
  for (const ch of text) {
    const candidate = line + ch
    if (line !== '' && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line)
      line = ch
    } else {
      line = candidate
    }
  }
  if (line !== '') lines.push(line)
  return lines
}

interface TitleLayout {
  lines: string[]
  fontSize: number
  lineHeight: number
}

/**
 * プロジェクト名を「100 文字まで全文表示」する前提でレイアウトする。
 *
 * - 100 文字（コードポイント）を超える分だけ末尾を "…" で省略
 * - 1 行に収まらなければフォントを段階的に縮小しつつ複数行に折り返す
 * - 最終段でも収まらない場合のみ、最終行を "…" で省略する
 *
 * ctx.font は呼び出し後に採用サイズのまま残るので、描画側でそのまま使える。
 */
const layoutTitle = (
  ctx: CanvasRenderingContext2D,
  projectName: string,
  maxWidth: number,
): TitleLayout => {
  const chars = [...projectName]
  const text =
    chars.length > TITLE.maxChars ? `${chars.slice(0, TITLE.maxChars).join('')}…` : projectName

  for (const [index, step] of TITLE.steps.entries()) {
    ctx.font = `bold ${step.size}px sans-serif`
    const lineHeight = Math.round(step.size * TITLE.lineHeightRatio)
    const lines = wrapText(ctx, text, maxWidth)

    if (lines.length <= step.maxLines) {
      return { lines: lines.length > 0 ? lines : [''], fontSize: step.size, lineHeight }
    }
    if (index === TITLE.steps.length - 1) {
      // 最小サイズでも収まらない極端な入力のみ、あふれた分を最終行に畳んで省略する
      const kept = lines.slice(0, step.maxLines)
      const overflow = lines.slice(step.maxLines).join('')
      kept[kept.length - 1] = truncateToWidth(ctx, kept[kept.length - 1] + overflow, maxWidth)
      return { lines: kept, fontSize: step.size, lineHeight }
    }
  }

  // steps は必ず 1 要素以上あるため到達しないが、型のためのフォールバック
  return { lines: [text], fontSize: FONT.title, lineHeight: FONT.title }
}

/** ボックス内にアスペクト比維持で画像を配置（letterbox 背景 + 角丸枠） */
const drawImageContain = (ctx: CanvasRenderingContext2D, image: HTMLImageElement, box: Box) => {
  ctx.fillStyle = COLORS.imageBg
  drawRoundedRect(ctx, box.x, box.y, box.w, box.h, 20)
  ctx.fill()

  const natW = Math.max(1, image.naturalWidth)
  const natH = Math.max(1, image.naturalHeight)
  const scale = Math.min(box.w / natW, box.h / natH)
  const drawW = natW * scale
  const drawH = natH * scale
  const drawX = box.x + (box.w - drawW) / 2
  const drawY = box.y + (box.h - drawH) / 2

  ctx.save()
  drawRoundedRect(ctx, box.x, box.y, box.w, box.h, 20)
  ctx.clip()
  ctx.drawImage(image, drawX, drawY, drawW, drawH)
  ctx.restore()

  ctx.strokeStyle = COLORS.border
  ctx.lineWidth = 2
  drawRoundedRect(ctx, box.x, box.y, box.w, box.h, 20)
  ctx.stroke()
}

/**
 * 「Before / 現況写真」「After / 提案イメージ」のセクション見出し。
 * accent=true で緑のピル + 太い緑下線（After 用）。返り値は見出しブロックの下端 y。
 */
const drawSectionLabel = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  main: string,
  sub: string,
  accent: boolean,
): number => {
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
  const baseline = y + FONT.sectionMain

  ctx.font = `bold ${FONT.sectionMain}px sans-serif`
  ctx.fillStyle = COLORS.text
  ctx.fillText(main, x, baseline)
  const mainW = ctx.measureText(main).width

  if (accent) {
    ctx.font = `bold ${FONT.sectionSub}px sans-serif`
    const subW = ctx.measureText(sub).width
    const padX = 22
    const pillH = FONT.sectionSub + 22
    const pillX = x + mainW + 28
    const pillY = baseline - FONT.sectionMain + (FONT.sectionMain - pillH) / 2 + 6
    ctx.fillStyle = COLORS.accentSoft
    drawRoundedRect(ctx, pillX, pillY, subW + padX * 2, pillH, pillH / 2)
    ctx.fill()
    ctx.fillStyle = COLORS.accent
    ctx.textBaseline = 'middle'
    ctx.fillText(sub, pillX + padX, pillY + pillH / 2 + 1)
    ctx.textBaseline = 'alphabetic'
  } else {
    ctx.font = `${FONT.sectionSub}px sans-serif`
    ctx.fillStyle = COLORS.muted
    ctx.fillText(sub, x + mainW + 24, baseline)
  }

  const underlineY = baseline + 22
  const underlineH = accent ? 8 : 4
  ctx.fillStyle = accent ? COLORS.accent : COLORS.border
  ctx.fillRect(x, underlineY, width, underlineH)

  return underlineY + underlineH
}

/**
 * 右上の会社ブランディングを描画（外枠なし）。
 * ロゴ画像があればロゴのみ（会社名はロゴ内に含まれ重複するため出さない）。
 * ロゴが無い時のみ会社名テキストを表示する（フォールバック）。
 */
const drawCompanyBranding = (
  ctx: CanvasRenderingContext2D,
  rightEdge: number,
  top: number,
  logo: HTMLImageElement | null,
  companyName: string,
) => {
  if (logo) {
    // ロゴはアスペクト比を保ったまま LOGO.maxW × LOGO.maxH に contain（PDF と同じく歪ませない）
    const ratio = Math.max(1, logo.naturalWidth) / Math.max(1, logo.naturalHeight)
    let logoH: number = LOGO.maxH
    let logoW = logoH * ratio
    if (logoW > LOGO.maxW) {
      logoW = LOGO.maxW
      logoH = logoW / ratio
    }
    ctx.drawImage(logo, rightEdge - logoW, top, logoW, logoH)
    return
  }

  if (companyName) {
    // ロゴ無しフォールバック: 会社名テキストを右上に。
    // タイトルの折返し幅はロゴ最大幅(LOGO.maxW)ぶんしか避けていないため同じ幅に制限し、
    // 1 行(約26文字)に収まらない社名は 2 行に折り返す。それでも超える分のみ末尾を省略する
    const maxCompanyW: number = LOGO.maxW
    const maxCompanyLines = 2
    ctx.fillStyle = COLORS.text
    ctx.textAlign = 'right'
    ctx.textBaseline = 'alphabetic'
    ctx.font = `bold ${FONT.company}px sans-serif`
    const lines = wrapText(ctx, companyName, maxCompanyW)
    const kept = lines.slice(0, maxCompanyLines)
    if (lines.length > maxCompanyLines) {
      const overflow = lines.slice(maxCompanyLines).join('')
      kept[maxCompanyLines - 1] = truncateToWidth(
        ctx,
        kept[maxCompanyLines - 1] + overflow,
        maxCompanyW,
      )
    }
    const lineHeight = Math.round(FONT.company * TITLE.lineHeightRatio)
    kept.forEach((line, i) => {
      ctx.fillText(line, rightEdge, top + FONT.company + i * lineHeight)
    })
    ctx.textAlign = 'left'
  }
}

/** 角丸ボックスに画像を cover（はみ出しを切り抜いて全面に充填）で配置 */
const drawImageCover = (
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  bx: number,
  by: number,
  size: number,
  radius: number,
) => {
  const natW = Math.max(1, image.naturalWidth)
  const natH = Math.max(1, image.naturalHeight)
  const scale = Math.max(size / natW, size / natH)
  const drawW = natW * scale
  const drawH = natH * scale
  ctx.save()
  drawRoundedRect(ctx, bx, by, size, size, radius)
  ctx.clip()
  ctx.drawImage(image, bx + (size - drawW) / 2, by + (size - drawH) / 2, drawW, drawH)
  ctx.restore()
}

/** 変換情報セクション（見出し + 色見本／素材見本行）を上から描画 */
const drawConversionInfo = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  rows: SummaryRow[],
  materialImages: Map<string, HTMLImageElement | null>,
) => {
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'

  ctx.font = `bold ${FONT.convHeading}px sans-serif`
  ctx.fillStyle = COLORS.text
  ctx.fillText(CONV_HEADING, x, y + FONT.convHeading)

  ctx.font = `${FONT.convSub}px sans-serif`
  ctx.fillStyle = COLORS.muted
  const subY = y + FONT.convHeading + FONT.convSub + 12
  ctx.fillText(CONV_SUB, x, subY)

  if (rows.length === 0) return

  const swatch = 44
  const swatchGap = 26
  const rowHeight = 70

  ctx.font = `bold ${FONT.row}px sans-serif`
  // ラベル列は上限を設けてクランプ（極端に長いカスタムエリア名で値の表示領域が潰れるのを防ぐ）。
  // 上限超過のラベルは描画時に truncateToWidth で省略される。
  const labelColMaxW = Math.round(width * 0.42)
  const labelColW = Math.min(
    labelColMaxW,
    Math.max(
      120,
      rows.reduce((acc, r) => Math.max(acc, ctx.measureText(r.label).width), 0),
    ),
  )
  const colon = '：'
  const colonW = ctx.measureText(colon).width
  // 値の表示幅は 0 未満にならないようフロアを設ける
  const valueMaxW = Math.max(0, width - (swatch + swatchGap + labelColW + 16 + colonW + 16))

  const rowsTop = subY + 36
  ctx.textBaseline = 'middle'

  rows.forEach((row, index) => {
    const rowMid = rowsTop + index * rowHeight + rowHeight / 2

    const sy = rowMid - swatch / 2
    const materialImage = row.thumbnailUrl ? materialImages.get(row.thumbnailUrl) : null
    if (row.kind === 'material') {
      // 素材は色見本と同じ枠に素材画像を切り抜いて充填。画像が無い時は薄いプレースホルダ枠
      if (materialImage) {
        drawImageCover(ctx, materialImage, x, sy, swatch, 8)
      } else {
        ctx.fillStyle = COLORS.imageBg
        drawRoundedRect(ctx, x, sy, swatch, swatch, 8)
        ctx.fill()
      }
      ctx.strokeStyle = COLORS.swatchBorder
      ctx.lineWidth = 1.5
      drawRoundedRect(ctx, x, sy, swatch, swatch, 8)
      ctx.stroke()
    } else if (row.kind === 'color' && row.hex) {
      ctx.fillStyle = row.hex
      drawRoundedRect(ctx, x, sy, swatch, swatch, 8)
      ctx.fill()
      ctx.strokeStyle = COLORS.swatchBorder
      ctx.lineWidth = 1.5
      drawRoundedRect(ctx, x, sy, swatch, swatch, 8)
      ctx.stroke()
    }

    const labelX = x + swatch + swatchGap
    ctx.font = `bold ${FONT.row}px sans-serif`
    ctx.fillStyle = COLORS.text
    ctx.fillText(truncateToWidth(ctx, row.label, labelColW), labelX, rowMid + 1)

    const colonX = labelX + labelColW + 16
    ctx.fillStyle = COLORS.muted
    ctx.fillText(colon, colonX, rowMid + 1)

    const valueX = colonX + colonW + 16
    ctx.font = `${FONT.row}px sans-serif`
    ctx.fillStyle = COLORS.text
    ctx.fillText(truncateToWidth(ctx, row.text, valueMaxW), valueX, rowMid + 1)
  })
}

const formatToday = (): string => {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}.${m}.${d}`
}

/**
 * Before / After / 変換情報 / 会社ブランディングを 1 枚の A4 横 PNG にまとめて Blob で返す。
 *
 * Canvas API のみで完結。R2 など外部 URL は convertToCorsProxyUrl 経由 + crossOrigin='anonymous' で
 * 読み込み、toBlob('image/png') で tainted エラーを回避する。ロゴは読込失敗しても全体を止めない。
 */
export const composeSummaryImage = async ({
  projectName,
  beforeImageUrl,
  afterImageUrl,
  rows,
  companyName,
  companyLogoUrl,
}: ComposeSummaryImageInput): Promise<Blob> => {
  // 素材行のサムネイル URL（重複排除）。読込失敗しても全体は止めない（枠のみ表示にフォールバック）
  const materialThumbUrls = [
    ...new Set(
      rows.filter(r => r.kind === 'material' && r.thumbnailUrl).map(r => r.thumbnailUrl as string),
    ),
  ]

  const [beforeImage, afterImage, logoImage, ...materialImageList] = await Promise.all([
    loadImage(beforeImageUrl),
    loadImage(afterImageUrl),
    companyLogoUrl ? loadImageSafe(companyLogoUrl) : Promise.resolve(null),
    ...materialThumbUrls.map(url => loadImageSafe(url)),
  ])
  const materialImages = new Map<string, HTMLImageElement | null>(
    materialThumbUrls.map((url, i) => [url, materialImageList[i] ?? null]),
  )

  const canvas = document.createElement('canvas')
  canvas.width = CANVAS.width
  canvas.height = CANVAS.height
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Failed to acquire 2D context')
  }

  // 背景
  ctx.fillStyle = COLORS.background
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  const { margin } = CANVAS
  const contentX = margin
  const contentRight = canvas.width - margin
  const contentW = contentRight - contentX

  // ── ヘッダー（左: タイトル / 右: 会社ブランディング） ──
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
  // タイトル幅は右上ロゴ領域を避けて一律に制限する（ロゴ有無でレイアウトを変えない）
  const titleMaxW = contentW - TITLE.reservedRightW
  const title = layoutTitle(ctx, projectName, titleMaxW)
  ctx.fillStyle = COLORS.text
  title.lines.forEach((line, i) => {
    ctx.fillText(line, contentX, margin + title.fontSize + i * title.lineHeight)
  })
  const titleBottom = margin + title.fontSize + (title.lines.length - 1) * title.lineHeight

  ctx.font = `${FONT.subtitle}px sans-serif`
  ctx.fillStyle = COLORS.muted
  ctx.fillText(SUBTITLE, contentX, titleBottom + FONT.subtitle + 18)

  if (companyName || logoImage) {
    drawCompanyBranding(ctx, contentRight, margin, logoImage, companyName ?? '')
  }

  // ── 本文レイアウト ──
  // ヘッダー実高（タイトル行数）から本文開始位置を決める。
  // 1 行のときは従来と同じ margin + 250 になる（76 + 36 + 18 + 120）。
  // 上限クランプは置かない: TITLE.steps が最大 3 行（titleBottom ≤ 330 → bodyTop ≤ 504）を
  // 保証しており、クランプは bodyTop だけを止めてサブタイトルと本文を食い込ませる逆効果になる。
  const bodyTop = titleBottom + FONT.subtitle + 18 + 120
  const footerHeight = 92
  // 最下部余白は marginBottom（< margin）で控えめにし、本文領域を下方向に広げる
  const footerTop = canvas.height - CANVAS.marginBottom - footerHeight
  const bodyBottom = footerTop - 36

  const columnGap = 90
  const leftColW = Math.round(contentW * 0.3)
  const rightColX = contentX + leftColW + columnGap
  const rightColW = contentRight - rightColX

  // 右カラム: After（大） + 注意書き
  const afterLabelBottom = drawSectionLabel(
    ctx,
    rightColX,
    bodyTop,
    rightColW,
    'After',
    '提案イメージ',
    true,
  )
  const afterImgTop = afterLabelBottom + 28
  ctx.font = `${FONT.disclaimer}px sans-serif`
  const disclaimerLines = wrapText(ctx, DISCLAIMER, rightColW)
  const disclaimerLineH = FONT.disclaimer + 12
  const disclaimerBlockH = disclaimerLines.length * disclaimerLineH
  const afterImgH = bodyBottom - afterImgTop - 24 - disclaimerBlockH
  drawImageContain(ctx, afterImage, {
    x: rightColX,
    y: afterImgTop,
    w: rightColW,
    h: afterImgH,
  })

  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
  ctx.font = `${FONT.disclaimer}px sans-serif`
  ctx.fillStyle = COLORS.faint
  disclaimerLines.forEach((line, i) => {
    ctx.fillText(
      line,
      rightColX,
      afterImgTop + afterImgH + 24 + i * disclaimerLineH + FONT.disclaimer,
    )
  })

  // 左カラム: Before（小） + 変換情報
  const beforeLabelBottom = drawSectionLabel(
    ctx,
    contentX,
    bodyTop,
    leftColW,
    'Before',
    '現況写真',
    false,
  )
  const beforeImgTop = beforeLabelBottom + 28
  const beforeImgH = Math.round((bodyBottom - beforeImgTop) * 0.46)
  drawImageContain(ctx, beforeImage, {
    x: contentX,
    y: beforeImgTop,
    w: leftColW,
    h: beforeImgH,
  })

  drawConversionInfo(ctx, contentX, beforeImgTop + beforeImgH + 56, leftColW, rows, materialImages)

  // ── フッター ──
  ctx.fillStyle = COLORS.border
  ctx.fillRect(contentX, footerTop, contentW, 2)
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
  ctx.font = `${FONT.footer}px sans-serif`
  ctx.fillStyle = COLORS.muted
  ctx.fillText(
    `${FOOTER_PREFIX} / ${formatToday()}`,
    contentX,
    footerTop + footerHeight / 2 + FONT.footer / 2,
  )

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(blob => {
      if (!blob) {
        reject(new Error('Failed to encode PNG'))
        return
      }
      resolve(blob)
    }, 'image/png')
  })
}
